import { Button } from "@/components/button";
import { useTrans } from "@/translations";

import React, { useCallback } from "react";
import { SketchPicker } from "react-color";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

export type COLOR_KEY =
  | "primaryColor"
  | "secondaryColor"
  | "unsortedColor"
  | "sortedColor"
  | "otherColor";

type COLOR = {
  key: COLOR_KEY;
  label: string;
};

export type COLOR_CONFIG = {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  unsortedColor?: string | null;
  sortedColor?: string | null;
  otherColor?: string | null;
};

export const CUSTOM_COLORS: COLOR[] = [
  {
    key: "primaryColor",
    label: "configLabels:primaryColor",
  },
  {
    key: "secondaryColor",
    label: "configLabels:secondaryColor",
  },
  {
    key: "unsortedColor",
    label: "configLabels:unsortedColor",
  },
  {
    key: "sortedColor",
    label: "configLabels:sortedColor",
  },
  {
    key: "otherColor",
    label: "configLabels:otherColor",
  },
];

export const DEFAULT_CONFIG = {
  primaryColor: "",
  secondaryColor: "",
  unsortedColor: "",
  sortedColor: "",
  otherColor: "",
};

type Props = {
  updatedColors: COLOR_CONFIG;
  setUpdatedColors: React.Dispatch<React.SetStateAction<COLOR_CONFIG>>;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
};

export const ColorsTable: React.FC<Props> = ({
  updatedColors,
  setUpdatedColors,
  setHasUnsavedChanges,
}: Props) => {
  const { t } = useTrans();

  const onColorChange = useCallback(
    (colorKey: string, color: string) => {
      setUpdatedColors((prev) => ({
        ...prev,
        [colorKey]: color,
      }));
      setHasUnsavedChanges(true);
    },
    [setUpdatedColors, setHasUnsavedChanges],
  );

  return (
    <table className="mt-6 mb-10 w-full table-auto text-sm">
      <thead>
        <tr className="h-12 border-b text-left">
          <th>
            <div className="mr-4">{t("configLabels:labelToRepresent")}</div>
          </th>
          <th>
            <div className="ml-4">{t("configLabels:color")}</div>
          </th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {CUSTOM_COLORS?.map(({ key, label }, index) => {
          return (
            <tr key={index} className="h-12 border-b last-of-type:border-none">
              <td>{t(label)}</td>

              <td>
                <Popover>
                  {({ close }) => (
                    <>
                      <PopoverButton>
                        <div className="mx-4 mt-2 flex min-w-[80px] items-center justify-center">
                          {updatedColors[key] ? (
                            <Square color={updatedColors[key] || ""} />
                          ) : (
                            <div className="-mt-2 cursor-pointer underline">
                              {t("actions:addColor")}
                            </div>
                          )}
                        </div>
                      </PopoverButton>
                      <PopoverPanel className="border-grey3 bg-grey1 absolute z-10 border p-4 shadow-sm">
                        <SketchPicker
                          color={updatedColors[key] ?? undefined}
                          onChange={(color) => onColorChange(key, color.hex)}
                          styles={{
                            default: {
                              picker: {
                                boxShadow: "none",
                              },
                            },
                          }}
                        />
                        <Button
                          className="mt-2 w-full"
                          label={t("common:done")}
                          onClick={close}
                        />
                      </PopoverPanel>
                    </>
                  )}
                </Popover>
              </td>
              <td>
                {updatedColors[key] && (
                  <div
                    className="ml-4 cursor-pointer underline"
                    onClick={() => {
                      setUpdatedColors((prev) => ({
                        ...prev,
                        [key]: "",
                      }));
                    }}
                  >
                    {t("actions:reset")}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const Square: React.FC<{ color: string }> = ({ color }) => {
  return (
    <div
      style={{
        width: 28,
        height: 28,
        backgroundColor: color,
      }}
    />
  );
};
